import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';

import type { Vacancy } from 'clients/AutoCompleteClient/types';
import type { UserResponse } from 'clients/UserClient/types';
import type { RobinJrStore, RobinJrStoreActions } from './robin-jr.store.types';

import { CampaignResponse } from 'clients/CampaignsClient/CampaignsClient.types';
import { UserFeatureToggles } from 'clients/UserClient/types';
import { queryClient } from 'config/queryClient';
import { createContextStore } from 'stores/createContextStore';
import { QueryKey } from 'types/query';
import { getVacancyName } from 'utils/vacancyEnrichedUtils';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

const initialState: Omit<RobinJrStore, keyof RobinJrStoreActions> = {
  selectedVacancy: null,
  selectedVacancies: {},
  selectedVacancyActiveType: 'ats',
  isRobinJrEnabled: false,
  isRobinJrFollowUpEnabled: false,
  hasInsufficientInfoError: false,
  aiOriginalMessage: undefined,
  isGeneratingMessage: false,
};

const RobinJrStoreContext = createContext<StoreApi<RobinJrStore> | null>(null);

const noop = () => {};
const defaultRobinJrStoreApi = createStore<RobinJrStore>()(() => ({
  ...initialState,
  setSelectedVacancy: noop,
  setSelectedVacancyActiveType: noop,
  setIsRobinJrEnabled: noop,
  setIsRobinJrFollowUpEnabled: noop,
  setHasInsufficientInfoError: noop,
  setAiOriginalMessage: noop,
  setIsGeneratingMessage: noop,
  reset: noop,
}));

export const useRobinJrSelectedVacancyOptional = (): Vacancy | null => {
  const store = useContext(RobinJrStoreContext);
  const defaultStoreRef = useRef(defaultRobinJrStoreApi);
  return useStore(store ?? defaultStoreRef.current, (s) => s.selectedVacancy);
};

export const RobinJrStoreProvider = ({
  children,
  currentUser,
  campaignId,
}: {
  children: React.ReactNode;
  currentUser: UserResponse;
  campaignId: number;
}) => {
  const [initialIsRobinJrUserEnabled] = useState(
    (currentUser.features?.find((f) => f.name === UserFeatureToggles.ROBIN_JR_GENERATE_REACHOUT_MESSAGE)?.enabled ??
      true) === true,
  );
  const [initialIsRobinJrFollowUpEnabled] = useState(
    (currentUser.features?.find((f) => f.name === UserFeatureToggles.ROBIN_JR_FOLLOW_UP)?.enabled ?? true) === true,
  );

  const [store] = useState(
    createStore<RobinJrStore>()(
      devtools(
        persist(
          (set, get) => ({
            ...initialState,
            isRobinJrEnabled: initialIsRobinJrUserEnabled,
            isRobinJrFollowUpEnabled: initialIsRobinJrFollowUpEnabled,
            setSelectedVacancy: (vacancy) => {
              const selectedVacancyType = get().selectedVacancyActiveType;

              if (!vacancy) {
                return set((s) => ({
                  ...initialState,
                  selectedVacancy: vacancy,
                  selectedVacancies: {
                    ...s.selectedVacancies,
                    [campaignId]: { ...s.selectedVacancies[campaignId], [selectedVacancyType]: vacancy },
                  },
                  selectedVacancyActiveType: s.selectedVacancyActiveType,
                  isRobinJrEnabled: s.isRobinJrEnabled,
                  isRobinJrFollowUpEnabled: s.isRobinJrFollowUpEnabled,
                }));
              }

              set((s) => ({
                selectedVacancies: {
                  ...s.selectedVacancies,
                  [campaignId]: { ...s.selectedVacancies[campaignId], [selectedVacancyType]: vacancy },
                },
                selectedVacancy: vacancy,
              }));
            },
            setSelectedVacancyActiveType: (vacancyType) => {
              const selectedVacancy = get().selectedVacancies[campaignId]?.[vacancyType] ?? null;

              set({ selectedVacancyActiveType: vacancyType, selectedVacancy });
            },
            setIsRobinJrEnabled: (isEnabled) => set(() => ({ isRobinJrEnabled: isEnabled })),
            setIsRobinJrFollowUpEnabled: (isEnabled) => set(() => ({ isRobinJrFollowUpEnabled: isEnabled })),
            setHasInsufficientInfoError: (hasError) => set(() => ({ hasInsufficientInfoError: hasError })),
            setAiOriginalMessage: (message) => set(() => ({ aiOriginalMessage: message })),
            setIsGeneratingMessage: (isGeneratingMessage) => set(() => ({ isGeneratingMessage })),
            reset: () => {
              set({
                hasInsufficientInfoError: false,
                aiOriginalMessage: undefined,
                isGeneratingMessage: false,
              });
            },
          }),
          {
            name: 'robin-jr-store-v3',
            partialize: (s) => ({
              selectedVacancies: s.selectedVacancies,
              selectedVacancyActiveType: s.selectedVacancyActiveType,
            }),
            storage: createJSONStorage(() => localStorage),
          },
        ),
        { name: 'robin-jr-store', enabled: true },
      ),
    ),
  );

  useEffect(() => {
    const campaign = queryClient.getQueryData<CampaignResponse>([
      QueryKey.campaignManagerContextUseCampaign,
      campaignId,
    ]);

    const state = store.getState();

    if (
      campaign?.connected_ats &&
      state.selectedVacancyActiveType === 'ats' &&
      (state.selectedVacancies[campaignId] === undefined || state.selectedVacancies[campaignId].ats === undefined)
    ) {
      state.setSelectedVacancy({
        ...campaign.connected_ats,
        name: getVacancyName(campaign.connected_ats),
        value: campaign.connected_ats.job_ats_id,
      });
    }
  }, []);

  return <RobinJrStoreContext.Provider value={store}>{children}</RobinJrStoreContext.Provider>;
};

export const useRobinJrStore = createContextStore(RobinJrStoreContext, 'RobinJrStoreContext');
